import { useEffect, useState } from 'react';
import { api } from '../api';
import ExportButton from '../components/ExportButton';

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: '已审核', color: 'bg-blue-100 text-blue-700' },
  invoiced: { label: '已开票', color: 'bg-purple-100 text-purple-700' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-red-100 text-red-700' },
};

export default function Sales() {
  const [list, setList] = useState<any[]>([]);
  const [deps, setDeps] = useState<any[]>([]);
  const [prods, setProds] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [form, setForm] = useState({ customer_name: '', department_id: '' });
  const [items, setItems] = useState<{ product_id: string; quantity: string; unit_price: string }[]>([{ product_id: '', quantity: '', unit_price: '' }]);

  const load = () => api('/sales/orders').then(setList).catch(() => {});
  useEffect(() => {
    load();
    api('/departments').then(setDeps).catch(() => {});
    api('/products').then(setProds).catch(() => {});
  }, []);

  // 创建订单
  const createOrder = async () => {
    if (!form.customer_name) return alert('请填写客户名称');
    const validItems = items.filter((it) => it.product_id && Number(it.quantity) > 0);
    if (validItems.length === 0) return alert('请至少添加一个有效明细');
    await api('/sales/orders', {
      method: 'POST',
      body: JSON.stringify({
        customer_name: form.customer_name,
        department_id: Number(form.department_id) || null,
        items: validItems.map((it) => ({
          product_id: Number(it.product_id),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price) || undefined,
        })),
      }),
    });
    setShow(false); resetForm(); load();
  };

  // 审核通过 → 自动生成财务收入 + 出库
  const approve = async (id: number) => {
    if (!confirm('确认审核通过？（将自动生成收入流水和出库记录）')) return;
    await api(`/sales/orders/${id}/approve`, { method: 'POST' }); load();
  };

  const del = async (id: number) => {
    if (!confirm('确认删除该订单？')) return;
    await api(`/sales/orders/${id}`, { method: 'DELETE' }); load();
  };

  const resetForm = () => {
    setForm({ customer_name: '', department_id: '' });
    setItems([{ product_id: '', quantity: '', unit_price: '' }]);
  };

  const addItem = () => setItems([...items, { product_id: '', quantity: '', unit_price: '' }]);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));

  // 计算订单金额
  const totalAmount = items.reduce((sum, it) => {
    const prod = prods.find((p) => p.id === Number(it.product_id));
    const price = Number(it.unit_price) || prod?.price || 0;
    return sum + Number(it.quantity || 0) * price;
  }, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">销售报货管理</h2>
          <p className="text-sm text-slate-500 mt-1">内勤收到报货单 → 审核 → 开票 → 发货闭环</p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            filename="销售报货单"
            fetchData={() => api('/sales/orders')}
            columns={[
              { header: '订单号', key: 'order_no' },
              { header: '客户', key: 'customer_name' },
              { header: '金额', key: 'total_amount' },
              { header: '状态', key: 'status', format: (r) => STATUS_MAP[r.status]?.label || r.status },
              { header: '部门', key: 'department_name' },
              { header: '创建人', key: 'creator' },
              { header: '创建时间', key: 'created_at' },
            ]}
          />
          <button onClick={() => { resetForm(); setShow(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700">
            + 新建报货单
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: '总订单数', value: list.length, color: 'text-slate-700' },
          { label: '待审核', value: list.filter((o) => o.status === 'pending').length, color: 'text-yellow-600' },
          { label: '已完成', value: list.filter((o) => o.status === 'completed').length, color: 'text-green-600' },
          { label: '总金额', value: '¥' + list.reduce((s, o) => s + (o.total_amount || 0), 0).toLocaleString(), color: 'text-blue-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-slate-500">{c.label}</div>
            <div className={`text-xl font-bold mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* 订单列表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left">
              <th className="p-3">订单号</th><th>客户</th><th>部门</th><th>金额</th><th>状态</th><th>创建时间</th><th className="w-32">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{o.order_no}</td>
                <td className="p-3 font-medium">{o.customer_name || '—'}</td>
                <td className="p-3"><span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{o.department_name || '—'}</span></td>
                <td className="p-3 font-medium text-green-600">¥{(o.total_amount || 0).toLocaleString()}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_MAP[o.status]?.color || 'bg-gray-100'}`}>
                    {STATUS_MAP[o.status]?.label || o.status}
                  </span>
                </td>
                <td className="p-3 text-xs text-slate-500">{(o.created_at || '').split(' ')[0]}</td>
                <td className="p-3 space-x-2">
                  <button onClick={() => setDetailId(detailId === o.id ? null : o.id)} className="text-brand-600 hover:underline text-xs">明细</button>
                  {o.status === 'pending' && (
                    <button onClick={() => approve(o.id)} className="text-green-600 hover:underline text-xs font-medium">审核</button>
                  )}
                  {['pending', 'approved'].includes(o.status) && (
                    <button onClick={() => del(o.id)} className="text-red-500 hover:underline text-xs">删除</button>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">暂无订单</td></tr>}
          </tbody>
        </table>
      </div>

      {/* 明细展开 */}
      {detailId && (() => {
        const order = list.find((o) => o.id === detailId);
        if (!order?.items) return null;
        return (
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-bold text-sm mb-2 text-blue-800">订单 {order.order_no} — 明细</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-blue-600 text-left">
                  <th className="p-2">产品编码</th><th>产品名</th><th>数量</th><th>单价</th><th>金额</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it: any) => (
                  <tr key={it.id} className="border-t border-blue-200">
                    <td className="p-2 font-mono text-xs">{it.code || '—'}</td>
                    <td className="p-2">{it.product_name}</td>
                    <td className="p-2">{it.quantity}</td>
                    <td className="p-2">¥{it.unit_price?.toLocaleString()}</td>
                    <td className="p-2 font-medium">¥{it.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* 新建弹窗 */}
      {show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShow(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">新建销售报货单</h3>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <input className="border rounded-lg px-3 py-2" placeholder="客户名称 *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              <select className="border rounded-lg px-3 py-2" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">选择部门</option>
                {deps.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm">订单明细</span>
                <button onClick={addItem} className="text-xs text-brand-600 hover:underline">+ 添加一行</button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className="col-span-4 border rounded px-2 py-1.5 text-sm" value={it.product_id} onChange={(e) => { const cp = [...items]; cp[i].product_id = e.target.value; setItems(cp); }}>
                    <option value="">选择产品</option>
                    {prods.map((p) => <option key={p.id} value={p.id}>{p.name} (¥{p.price})</option>)}
                  </select>
                  <input className="col-span-2 border rounded px-2 py-1.5 text-sm" type="number" placeholder="数量" value={it.quantity} onChange={(e) => { const cp = [...items]; cp[i].quantity = e.target.value; setItems(cp); }} />
                  <input className="col-span-3 border rounded px-2 py-1.5 text-sm" type="number" placeholder="单价(可选)" value={it.unit_price} onChange={(e) => { const cp = [...items]; cp[i].unit_price = e.target.value; setItems(cp); }} />
                  <span className="col-span-2 text-sm text-slate-600">¥{((Number(it.quantity) || 0) * (Number(it.unit_price) || prods.find((p) => p.id === Number(it.product_id))?.price || 0)).toLocaleString()}</span>
                  <button onClick={() => removeItem(i)} className="col-span-1 text-red-500 text-sm">×</button>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center mb-4 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">订单合计：</span>
              <span className="text-xl font-bold text-green-600">¥{totalAmount.toLocaleString()}</span>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShow(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={createOrder} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">提交订单</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
